import { useContext, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { StyledDialog } from 'theme/index';
import { autoCreateTaskAPI } from 'services/secureMessageServices';
import { AuthContext } from 'utils/AuthContext';
import { useSiblings } from 'components/SiblingSwitch/useSiblings';
import { formatSiblingName } from 'components/SiblingSwitch/siblingProfile';

/** Upper bound for the optional questions. */
const MAX_QUESTIONS_LENGTH = 1000;
/** Task template key in secure-messaging-service (mindweal.task.autoAssign.mapping). */
const PENDING_BALANCE_EVENT = 'PENDING-BALANCE';

/**
 * Props for {@link RequestBalanceDialog}.
 * open      - whether the dialog is shown
 * patientId - patient whose outstanding balance is requested
 * onClose   - closes the dialog
 */
interface RequestBalanceDialogProps {
  open: boolean;
  patientId: number;
  onClose: () => void;
}

/**
 * MM-1130: "Additional Billing Questions" dialog opened from the Request Your Balance tile. The
 * questions field is optional. Submit calls the existing POST /task/auto-create with eventType
 * PENDING-BALANCE and the questions as remarks, which creates a Billing task for the billing team; on
 * success the dialog shows the confirmation message.
 */
const RequestBalanceDialog = ({ open, patientId, onClose }: RequestBalanceDialogProps) => {
  const { user } = useContext(AuthContext);
  /** Child whose profile is open; its name goes into the task title. */
  const { activeSibling } = useSiblings();
  const [questions, setQuestions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /**
   * Closes the dialog and resets it for the next request, unless a submit is in flight.
   */
  const handleClose = () => {
    if (submitting) return;
    setQuestions('');
    setSubmitted(false);
    onClose();
  };

  /**
   * Sends the balance request; questions may be empty.
   */
  const handleSubmit = () => {
    setSubmitting(true);
    autoCreateTaskAPI({
      patientId,
      userId: user?.userId,
      // Fallback if the sibling list has not loaded yet, so the task title is never blank
      patientName: formatSiblingName(activeSibling) || `Patient ID ${patientId}`,
      eventType: PENDING_BALANCE_EVENT,
      remarks: questions.trim(),
    })
      .then(() => setSubmitted(true))
      .catch((error: any) => {
        toast.error(
          error?.response?.data?.errorMsg ?? 'We could not submit your request. Please try again.'
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    // MM-1130 feedback: fixed sm width (title fits on one line on desktop) and smaller side margins on phones
    <StyledDialog
      open={open}
      onClose={handleClose}
      aria-labelledby='request-balance-title'
      fullWidth
      maxWidth='sm'
      sx={{ '& .MuiDialog-paper': { mx: { xs: 2, sm: 4 } } }}
    >
      <DialogTitle
        id='request-balance-title'
        // MM-1130 feedback: keep the title on one line; smaller font on phones
        sx={{
          padding: { xs: '16px 32px 0', sm: '16px 48px 0' },
          fontSize: { xs: 16, sm: 24 },
          whiteSpace: 'nowrap',
        }}
      >
        {submitted ? 'Request Submitted' : 'Additional Billing Questions'}
        <IconButton
          aria-label='close'
          onClick={handleClose}
          disabled={submitting}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {submitted ? (
          <>
            <Typography variant='subtitle1' mt={2}>
              Your request has been submitted. We’ll respond within one business day by text message or MindWeal
              Secure Chat in the Billing channel.
            </Typography>
            {/* Compact, centred OK rather than full width */}
            <Box textAlign='center' mt={3}>
              <Button sx={{ minWidth: 120 }} onClick={handleClose}>
                OK
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant='subtitle1' mt={2}>
              Submit your balance request below. If you have any additional questions, enter them here.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              // The theme fixes MuiOutlinedInput at height 44 (single-line fields); let this text area grow to
              // its rows so the text and placeholder sit inside the box, starting at the top.
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { height: 'auto', alignItems: 'flex-start' } }}
              placeholder='Enter your questions here (optional).'
              value={questions}
              onChange={(e) => setQuestions(e.target.value.slice(0, MAX_QUESTIONS_LENGTH))}
              disabled={submitting}
              inputProps={{ 'aria-label': 'Additional billing questions (optional)' }}
            />
            {/* MM-1130 feedback: centred, sized to its label instead of full width; label shortened to "Submit" */}
            <Box textAlign='center' mt={3}>
              <Button
                sx={{ px: 4 }}
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : null}
              >
                Submit
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default RequestBalanceDialog;
